import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { Plus, MoreHorizontal } from "lucide-react";
import {
    listSystemUsers,
    createSystemUser,
    rotateSystemUserToken,
    deactivateSystemUser,
    reactivateSystemUser,
} from "./api";
import Table from "../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { useTableSavedViews, TableSavedViewsControl } from "../../TableSavedViews";
import RevealTokenModal from "./RevealTokenModal";
import ConfirmModal from "./ConfirmModal";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const TABLE_HEADERS = ["Selection", "Action", "Label", "Email", "Created", "Rotated", "Expires", "Status"];

const SORTABLE_COLUMNS = {
    "Label": "label",
    "Email": "email",
    "Created": "createdAt",
    "Rotated": "rotatedAt",
    "Expires": "expiresAt",
    "Status": "active",
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const SystemUsersTable = ({ officeCode, officeName }) => {
    const [systemUsers, setSystemUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reveal, setReveal] = useState(null);
    const [newLabel, setNewLabel] = useState("");
    const [creating, setCreating] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // { type: "rotate" | "deactivate", row }
    const [selectedRows, setSelectedRows] = useState([]);
    const [sortBy, setSortBy] = useState(null);
    const [ascending, setAscending] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
    const [openDropdownRow, setOpenDropdownRow] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    const dropdownRef = useRef(null);
    const checkboxHeaderRef = useRef(null);

    const tableViews = useTableSavedViews({
        pageKey: "system-users-list",
        allHeaders: TABLE_HEADERS,
        fixedPrefixCount: 2,
        defaultStickyColumnKeys: ["Selection", "Action", "Label"],
        getExtraConfig: () => ({ currentPage: page, pageSize }),
        onApplyConfig: (config) => {
            if (config.currentPage) setPage(config.currentPage);
            if (config.pageSize) setPageSize(config.pageSize);
        },
    });

    const {
        columnWidths: savedColumnWidths,
        setColumnWidths: onColumnResize,
        dataHeaders,
        controlLabel,
        views,
        activeView,
        loading: viewsLoading,
        isDirty,
        selectView,
        saveNewView,
        updateActiveView,
        renameView,
        deleteView,
        setDefaultView,
        updateColumnVisibility,
        markDirty,
        columnManagerProps,
        onManageColumns,
    } = tableViews;

    const refresh = useCallback(() => {
        setLoading(true);
        listSystemUsers(officeCode)
            .then((res) => setSystemUsers(res.data || []))
            .catch(() => {
                setSystemUsers([]);
                notify.error({ title: "Failed to load system users" });
            })
            .finally(() => setLoading(false));
    }, [officeCode]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const handle = (e) => {
            if (e.target.closest("[data-action-trigger]")) return;
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdownRow(null);
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newLabel.trim()) {
            notify.error({ title: "A label is required (e.g. \"ACME Shipper EDI\")" });
            return;
        }
        setCreating(true);
        try {
            const res = await createSystemUser(officeCode, newLabel.trim(), []);
            setNewLabel("");
            setReveal(res.data);
            refresh();
        } catch (err) {
            notify.error({ title: "Failed to create system user", message: err?.response?.data?.content || "" });
        } finally {
            setCreating(false);
        }
    };

    const handleRotate = (row) => setPendingAction({ type: "rotate", row });
    const handleDeactivate = (row) => setPendingAction({ type: "deactivate", row });

    const handleReactivate = async (row) => {
        try {
            await reactivateSystemUser(row.userId);
            notify.success({ title: `"${row.label}" reactivated — rotate its token to get a new working credential.` });
            refresh();
        } catch (err) {
            notify.error({ title: "Failed to reactivate", message: err?.response?.data?.content || "" });
        }
    };

    const confirmPendingAction = async () => {
        if (!pendingAction) return;
        const { type, row } = pendingAction;
        setPendingAction(null);
        if (type === "rotate") {
            try {
                const res = await rotateSystemUserToken(row.userId);
                setReveal(res.data);
                refresh();
            } catch (err) {
                notify.error({ title: "Failed to rotate token", message: err?.response?.data?.content || "" });
            }
        } else if (type === "deactivate") {
            try {
                await deactivateSystemUser(row.userId);
                notify.success({ title: "System user deactivated" });
                refresh();
            } catch (err) {
                notify.error({ title: "Failed to deactivate", message: err?.response?.data?.content || "" });
            }
        }
    };

    const handleSort = (columnHeader) => {
        if (sortBy === columnHeader) {
            setAscending(!ascending);
        } else {
            setSortBy(columnHeader);
            setAscending(true);
        }
        setPage(1);
        markDirty();
    };

    const handleActionClick = (id, e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
        setOpenDropdownRow((prev) => (prev === id ? null : id));
    };

    const handleSelectAll = (checked) => setSelectedRows(checked ? systemUsers.map((u) => u.userId) : []);
    const handleSingleCheckbox = (id) =>
        setSelectedRows((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

    let effective = systemUsers;
    if (sortBy && SORTABLE_COLUMNS[sortBy]) {
        const key = SORTABLE_COLUMNS[sortBy];
        effective = [...systemUsers].sort((a, b) => {
            let aVal = a[key];
            let bVal = b[key];
            if (key === "active") {
                aVal = aVal ? 1 : 0;
                bVal = bVal ? 1 : 0;
                return ascending ? aVal - bVal : bVal - aVal;
            }
            if (key === "createdAt" || key === "rotatedAt" || key === "expiresAt") {
                aVal = aVal ? new Date(aVal).getTime() : 0;
                bVal = bVal ? new Date(bVal).getTime() : 0;
                return ascending ? aVal - bVal : bVal - aVal;
            }
            aVal = (aVal ?? "").toString().toLowerCase();
            bVal = (bVal ?? "").toString().toLowerCase();
            return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
    }

    const totalPages = Math.max(1, Math.ceil(effective.length / pageSize));
    const paged = effective.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="bg-white dark:bg-acs-dark-surface flex flex-col transition-none rounded-xl border border-gray-200 dark:border-acs-dark-border overflow-hidden">
            <form onSubmit={handleCreate} className="flex items-end gap-2 p-fluid-3 border-b border-gray-200 dark:border-acs-dark-border">
                <div className="flex-1 max-w-[420px]">
                    <label className="block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text mb-1">
                        New system user label
                    </label>
                    <input
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        placeholder='e.g. "ACME Shipper EDI"'
                        className="w-full border border-gray-300 dark:border-acs-dark-border rounded-xl px-3 py-2 text-fluid-sm bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text placeholder-gray-400 dark:placeholder-acs-dark-subtext focus:outline-none focus:border-[#223688] dark:focus:border-[#31C4AB] shadow-sm"
                    />
                </div>
                <button
                    type="submit"
                    disabled={creating}
                    className="flex items-center gap-1.5 px-fluid-button-x py-fluid-button-y bg-[#1e2a74] hover:bg-[#18225c] disabled:opacity-50 text-white text-fluid-sm rounded font-semibold"
                >
                    <Plus size={16} /> {creating ? "Creating..." : "Add System User"}
                </button>
            </form>

            <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
                <TableSavedViewsControl
                    controlLabel={controlLabel}
                    views={views}
                    activeView={activeView}
                    loading={viewsLoading}
                    error={null}
                    isDirty={isDirty}
                    allDataHeaders={TABLE_HEADERS.slice(2)}
                    dataHeaders={dataHeaders}
                    onSelectView={selectView}
                    onSaveNew={saveNewView}
                    onUpdateActive={updateActiveView}
                    onRename={renameView}
                    onDelete={deleteView}
                    onSetDefault={setDefaultView}
                    onUpdateColumnVisibility={updateColumnVisibility}
                    onManageColumns={onManageColumns}
                />
            </div>

            {loading ? (
                <Loader color="text-blue-600" message="Loading system users..." />
            ) : (
                <div className="flex-1 min-h-[200px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
                    <Table
                        {...columnManagerProps}
                        headers={TABLE_HEADERS}
                        data={paged}
                        initialColumnWidths={savedColumnWidths}
                        onColumnResize={onColumnResize}
                        onSelectAll={handleSelectAll}
                        renderCheckboxActions={() => {}}
                        checkboxHeaderRef={checkboxHeaderRef}
                        selectedRows={selectedRows}
                        enableColumnResize
                        enableColumnReorder
                        enableColumnPin
                        nonHideableColumnIds={["Selection", "Action", "Label"]}
                        sortBy={sortBy}
                        ascending={ascending}
                        onSort={handleSort}
                        sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
                    >
                        {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
                            const isOpen = openDropdownRow === row.userId;

                            const actions = [
                                ...(row.active
                                    ? [
                                        { icon: "RotateCw", label: "Rotate Token", color: "blue", action: () => handleRotate(row) },
                                        { icon: "Ban", label: "Deactivate", color: "red", action: () => handleDeactivate(row) },
                                    ]
                                    : [
                                        { icon: "ShieldCheck", label: "Reactivate", color: "green", action: () => handleReactivate(row) },
                                    ]),
                            ];

                            return (
                                <>
                                    <td
                                        className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                                        style={{ width: "80px", minWidth: "80px", maxWidth: "80px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                                    >
                                        <input
                                            type="checkbox"
                                            className="cursor-pointer mx-auto block"
                                            checked={selectedRows.includes(row.userId)}
                                            onChange={() => handleSingleCheckbox(row.userId)}
                                        />
                                    </td>

                                    <td
                                        onClick={(e) => e.stopPropagation()}
                                        className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                                        style={{ width: "50px", minWidth: "50px", maxWidth: "50px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                                    >
                                        <button
                                            data-action-trigger
                                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                                            onClick={(e) => handleActionClick(row.userId, e)}
                                        >
                                            <MoreHorizontal size={18} className="mx-auto" />
                                        </button>
                                    </td>

                                    {isOpen &&
                                        ReactDOM.createPortal(
                                            <div ref={dropdownRef} style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
                                                <ActionMenu
                                                    rowId={row.userId}
                                                    isOpen
                                                    onClose={() => setOpenDropdownRow(null)}
                                                    moduleName="SystemUser"
                                                    actions={actions}
                                                />
                                            </div>,
                                            document.body
                                        )}

                                    {(() => {
                                        const orderedDataHeaders = (visibleColumns ?? [])
                                            .map((col) => col.id)
                                            .filter((id) => id !== "Selection" && id !== "Action");
                                        const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                                        return dataHeadersToRender.map((header) => {
                                            const { className: colClass, style: colStyle } = getStickyCellProps(header);
                                            let content;
                                            switch (header) {
                                                case "Label":
                                                    content = row.label;
                                                    break;
                                                case "Email":
                                                    content = row.email;
                                                    break;
                                                case "Created":
                                                    content = formatDate(row.createdAt);
                                                    break;
                                                case "Rotated":
                                                    content = formatDate(row.rotatedAt);
                                                    break;
                                                case "Expires":
                                                    content = formatDate(row.expiresAt);
                                                    break;
                                                case "Status":
                                                    content = row.active ? (
                                                        <span className="text-green-700 dark:text-green-400 font-medium">Active</span>
                                                    ) : (
                                                        <span className="text-gray-400 dark:text-acs-dark-subtext font-medium">Inactive</span>
                                                    );
                                                    break;
                                                default:
                                                    content = "--";
                                            }
                                            return (
                                                <td
                                                    key={header}
                                                    className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                                                    style={{ ...colStyle, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                                >
                                                    {content}
                                                </td>
                                            );
                                        });
                                    })()}
                                </>
                            );
                        }}
                    </Table>
                </div>
            )}

            <GlobalPagination
                label="System users per page"
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
                options={PAGE_SIZE_OPTIONS}
            />

            {reveal && (
                <RevealTokenModal
                    label={reveal.label}
                    officeCode={officeName || officeCode}
                    refreshToken={reveal.refreshToken}
                    expiresAt={reveal.expiresAt}
                    onClose={() => setReveal(null)}
                />
            )}

            {pendingAction && (
                <ConfirmModal
                    title={pendingAction.type === "rotate" ? "Rotate token?" : "Deactivate system user?"}
                    message={
                        pendingAction.type === "rotate"
                            ? `Rotate the token for "${pendingAction.row.label}"? The current token will stop working immediately.`
                            : `Deactivate "${pendingAction.row.label}"? All of its tokens will stop working immediately. This cannot be undone.`
                    }
                    confirmLabel={pendingAction.type === "rotate" ? "Rotate" : "Deactivate"}
                    danger={pendingAction.type === "deactivate"}
                    onConfirm={confirmPendingAction}
                    onCancel={() => setPendingAction(null)}
                />
            )}
        </div>
    );
};

export default SystemUsersTable;
